import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  setAuthenticated,
  setError,
  setLoading,
} from "@/features/auth/interactiveSurveySlice";
import {
  saveInteractiveToken,
  useLoginWithPasscodeMutation,
} from "@/services/interactiveSurveyAuth";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

interface PasscodeInputProps {
  surveyId: string;
}

const PasscodeInput = ({ surveyId }: PasscodeInputProps) => {
  const DASH = "−";
  const [passcode, setPasscode] = useState(DASH + DASH + DASH + DASH);
  const [inputState, setInputState] = useState<"default" | "success" | "error">(
    "default",
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state.interactiveSurvey);
  const [loginWithPasscode] = useLoginWithPasscodeMutation();
  const [cursor, setCursor] = useState(0);

  // Auto-focus
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.selectionEnd = cursor;
    }
  }, [inputRef, cursor]);

  // Handle authentication submission when 4 characters are entered
  const handleAuthentication = useCallback(
    async (code: string) => {
      if (code.length !== 4) return;

      dispatch(setLoading(true));

      try {
        const result = await loginWithPasscode({
          passcode: code,
          surveyId: surveyId,
        }).unwrap();

        // Success state
        setInputState("success");
        dispatch(
          setAuthenticated({ token: result.interactiveToken, surveyId }),
        );
        saveInteractiveToken(result.interactiveToken, surveyId);

        // Auto-redirect after 800ms as per documentation
        setTimeout(() => {
          navigate(`/interactive/${surveyId}/cover`);
        }, 800);
      } catch {
        // Error state
        setInputState("error");
        dispatch(setError("Incorrect passcode"));

        // Reset input field after showing error
        setTimeout(() => {
          setPasscode("");
          setInputState("default");
          setCursor(0);
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 1500);
      }
    },
    [dispatch, loginWithPasscode, surveyId, navigate],
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let value = "";
    let cursorPosition = 0;
    for (let i = 0; i < 4; i += 1) {
      value += e.currentTarget.value[i] || DASH;
      if (e.currentTarget.value[i] && e.currentTarget.value[i] !== DASH) {
        cursorPosition += 1;
      }
    }

    setCursor(cursorPosition);

    // Only allow alphanumeric characters, limit to 4
    const sanitized = value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 4);
    setPasscode(sanitized.padEnd(4, DASH));

    // Auto-submit when 4 characters are entered
    if (sanitized.length === 4) {
      handleAuthentication(sanitized);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Prevent cursor movement via keyboard as per documentation
    if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
      e.preventDefault();
      if (inputRef.current) {
        inputRef.current.selectionEnd = cursor;
        inputRef.current.selectionStart = cursor;
      }
    }
  };

  const handleMouseEvent = (e: React.MouseEvent<HTMLInputElement>) => {
    // don't move cursor for mouse clicks
    e.preventDefault();
    if (inputRef.current) {
      inputRef.current.selectionEnd = cursor;
      inputRef.current.selectionStart = cursor;
    }
  };

  // Dynamic styling based on state
  const getInputClasses = () => {
    const baseClasses =
      "w-[242px] h-[78px] text-center text-4xl rounded-lg border-2 outline-none transition-all duration-200";

    switch (inputState) {
      case "success":
        return `${baseClasses} bg-cbre-green-semidark text-white`;
      case "error":
        return `${baseClasses} bg-orange-300 text-white`;
      default:
        return `${baseClasses}  bg-blue-200 text-blue-500 border-blue-500 focus:border-blue-400`;
    }
  };

  const getStatusMessage = () => {
    switch (inputState) {
      case "success":
        return "Correct! Welcome!";
      case "error":
        return "Incorrect passcode";
      default:
        return null;
    }
  };

  return (
    <div style={{ marginTop: "1rem", textAlign: "center" }}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          // value={passcode}
          value={passcode.length === 0 ? DASH + DASH + DASH + DASH : passcode}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onMouseUp={handleMouseEvent}
          className={getInputClasses()}
          disabled={isLoading || inputState === "success"}
          style={{
            fontSize: "3rem",
            fontWeight: "600",
            letterSpacing: "0.8rem",
          }}
        />
      </div>

      {/* Status message */}
      {getStatusMessage() && (
        <div
          className={`text-sm font-medium ${inputState === "error" ? "text-orange-600" : "text-green-600"}`}
        >
          {getStatusMessage()}
        </div>
      )}

      {isLoading && <div className="text-sm text-gray-600">Verifying...</div>}
    </div>
  );
};

export default PasscodeInput;
